import React, { useCallback, useMemo } from 'react';

import config from 'config';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { FormattedMessage } from '@techstyle/react-intl';

import { desktop, mobile } from '../../styles';
import { BasicButton } from '../../styles/button';
import isNewPricingPromoBannersFlagOn from '../../utils/isNewPricingPromoBannersFlagOn';
import { useLDFlags } from '../../utils/LD/useLDFlags';
import { normalizeProducts } from '../../utils/normalizeProductsData';
import { useMembership } from '../../utils/useMembership';
import { Component as ProductCell } from '../ProductCell';
import ProductCellBMIG from '../ProductCellBMIG';
import { Component as ProductImage } from '../ProductImage';
import { GuestPrice, MemberPrice } from '../ProductPrice/ProductPrice';
import { Component as Carousel } from '../ProductTrackingCarousel';
import StyleContext from '../StyleContext/StyleContext';

const leadOnly = config.get('public.productTags.leadOnly');
const vipOnly = config.get('public.productTags.vipOnly');

const Container = styled.div`
  margin: 0 auto;
  max-width: 100vw;
  overflow-x: auto;
  padding-top: ${props => props.theme.spacing.moderate}px;
  text-align: center;

  ${desktop`
    padding-top: 0;
  `};

  ${props =>
    props.shouldUsePairItWith &&
    `
    @media (max-width: ${props.theme.breakpoints.medium}px) {
      padding: 0;
    }
  `}

  ${props =>
    props.$isPairItWith &&
    css`
      background-color: ${props => props.theme.colors.gunmetal};
      color: ${props => props.theme.colors.white};

      ${ProductCellBMIG.ProductName}, ${ProductCellBMIG.ProductPrice}, ${GuestPrice}, ${MemberPrice}, ${ProductCellBMIG.BundlePromoLabel}, ${ProductCellBMIG.OptionText}, ${ProductCellBMIG.PromoLabel} {
        color: ${props => props.theme.colors.white};
      }

      ${ProductCellBMIG.ProductPrice} > span {
        color: ${props => props.theme.colors.white};
      }
    `}
`;

const Title = styled.div`
  margin-bottom: ${props => props.theme.spacing.moderate}px;
  text-align: center;

  ${props =>
    props.variantStyle &&
    css`
      font-size: 24px;
      font-weight: 500;
      letter-spacing: 0;
      margin-top: 54px;
      text-transform: none;

      ${mobile`
        font-size: 18px;
        font-weight: bold;
        letter-spacing: -0.3px
      `}
    `}

  ${({ theme }) => theme.header.variants.h2.textStyles}
`;

const ModifiedRecommendationsTitle = styled(Title)`
  @media (max-width: ${props => props.theme.breakpoints.medium}px) {
    display: none;
  }
`;

const ModifiedMobileRecommendationsTitle = styled(Title)`
  display: none;

  @media (max-width: ${props => props.theme.breakpoints.medium}px) {
    display: block;
  }
`;

const ProductList = styled(Carousel)`
  max-width: 100%;
  padding-bottom: 8px;

  .slick-slider {
    padding-bottom: 40px;

    ${props =>
      props.$isPairItWith &&
      css`
        .slick-dots > li.slick-active button:before {
          background: ${props.theme.colors.white};
        }
        .slick-dots > li > button:before {
          border-color: ${props.theme.colors.white};
        }
        .slick-arrow {
          border-color: ${props.theme.colors.white};
          color: ${props.theme.colors.gunmetal};
        }
      `}
  }
`;

const CurrentItemWrapper = styled.div`
  border-right: solid 1px ${props => props.theme.colors.gray250};
  display: inline-block;
  margin-top: 71px;
  padding: ${props => props.theme.spacing.small}px
    ${props => props.theme.spacing.moderate}px 70px 0;
  vertical-align: top;
  width: 176px;

  padding-left: 16px;

  @media (max-width: ${props => props.theme.breakpoints.large}px) {
    width: 156px;
  }

  @media (max-width: ${props => props.theme.breakpoints.medium}px) {
    margin-top: 0px;
    width: calc(50% + -28px);
  }
`;

const CurrentItemLabel = styled.div`
  ${({ theme }) => theme.paragraph.variants.paragraph4Uppercase.textStyles}

  display: block;
  padding-bottom: ${props => props.theme.spacing.tiny}px;

  @media (max-width: ${props => props.theme.breakpoints.medium}px) {
  }
`;

const CarouselWrapper = styled.div`
  display: inline-block;
  overflow: hidden;
  padding: 0 ${props => props.theme.spacing.moderate}px;
  width: 470px;

  @media (max-width: ${props => props.theme.breakpoints.large - 1}px) {
    margin-left: 0;
    width: 204px;
  }

  @media (max-width: ${props => props.theme.breakpoints.medium}px) {
    width: calc(50% + 25px);
  }
`;

const StyledProductImage = styled(ProductImage)`
  background-color: ${props => props.theme.colors.white};
  height: auto;
`;

const StyledButton = styled(BasicButton)`
  border: 1px solid ${props => props.theme.colors.active};
  color: ${props => props.theme.colors.active};
  font-weight: normal;
  margin-top: ${props => props.theme.spacing.small}px;
  min-width: 0;
  width: calc(100% - 2px);
`;

const StyledProductCellBMIG = styled(ProductCellBMIG)`
  ${props =>
    props.shouldShowSpacedBorder &&
    `
    margin: 3px;
  `}

  ${ProductCellBMIG.ThumbnailWrapper} {
    ${props =>
      props.shouldShowSpacedBorder &&
      `
      border-radius: 2px;
      box-shadow: 0px 2px 6px ${props.theme.colors.lightShadow1};
      box-sizing: border-box;
      padding: '10'px;

      @media (max-width: ${props.theme.breakpoints.large}px) {
        .slick-active & {
          border: none;
          box-shadow: 0px 2px 4px ${props.theme.colors.lightShadow1};
        }
      }
    `}
  }

  ${ProductCellBMIG.ProductImage} {
    ${props =>
      props.shouldShowShadow &&
      `box-shadow: 0 1px 2px 0 ${props.theme.colors.lightShadow1};`}
  }
`;

const ProductRecommendations = ({
  maxProducts,
  carouselElementRef,
  className,
  ctaText,
  hideColorsAvailable,
  hideFlags,
  hideOverlay,
  hidePrice,
  hidePromo,
  hideReviewStars,
  hideTitle,
  onCtaClick,
  onProductClick,
  options = {
    dots: true,
    slidesToShow: 4,
    slidesToScroll: 4,
    infinite: false,
    arrows: false,
  },
  product,
  products = [],
  pSource,
  shouldNotBeClickable,
  shouldShowCta,
  shouldShowProductColor,
  shouldShowSpacedBorder,
  shouldUseBmigComponents,
  shouldUsePairItWith: shouldUsePairItWithProps,
  title,
  trackingSource,
  variantStyle,
}) => {
  const extraQueryParams = { psrc: pSource };
  const { isVip } = useMembership();
  const router = useRouter();
  const { psrc } = router.query;

  const shouldUsePairItWith =
    shouldUsePairItWithProps || pSource === 'pair_it_with';

  const {
    'new-product-pricing': shouldDisplayNewProductPricing,
    'experiment-new-pricing-promo-banners': newPricingPromoBannersFlag,
  } = useLDFlags();
  const displayNewProductPricing =
    psrc !== 'sale-secondary' &&
    (isNewPricingPromoBannersFlagOn(newPricingPromoBannersFlag)
      ? newPricingPromoBannersFlag.newPricing
      : shouldDisplayNewProductPricing);

  const elementsToHide = useMemo(
    () => ({
      price: hidePrice,
      colorsAvailable: hideColorsAvailable,
      flags: hideFlags,
      overlay: hideOverlay,
      reviewStars: hideReviewStars,
      promo: hidePromo,
      wishlist: true,
    }),
    [
      hideColorsAvailable,
      hideFlags,
      hideOverlay,
      hidePrice,
      hidePromo,
      hideReviewStars,
    ]
  );

  const filterProductsByMembership = useCallback(
    products => {
      const productTagToExclude = isVip ? leadOnly : vipOnly;
      return products.filter(product => {
        const { tag_id_list: tagIdList = [] } = product || {};
        return !tagIdList.includes(productTagToExclude);
      });
    },
    [isVip]
  );

  const productList = useMemo(() => {
    const filteredProducts = filterProductsByMembership(products);
    const productsToShow = filteredProducts.slice(0, maxProducts);

    return shouldUseBmigComponents
      ? normalizeProducts({ products: productsToShow })
      : productsToShow;
  }, [
    filterProductsByMembership,
    maxProducts,
    products,
    shouldUseBmigComponents,
  ]);

  const renderProductSlides = useCallback(
    addRef => {
      if (shouldUseBmigComponents) {
        return productList.map((product, index) => (
          <React.Fragment key={product.masterProductId}>
            <div
              id={product.masterProductId}
              ref={ref => addRef(product.masterProductId, ref)}
            >
              <StyledProductCellBMIG
                gridIndex={index + 1}
                product={product}
                elementsToHide={elementsToHide}
                shouldShowShadow={variantStyle}
                onProductClick={onProductClick}
                productSource={trackingSource}
                shouldShowSpacedBorder={shouldShowSpacedBorder}
                shouldDisplayNewProductPricing={displayNewProductPricing}
              />
            </div>
            {shouldShowCta ? (
              <StyledButton secondary onClick={() => onCtaClick(product)}>
                {ctaText}
              </StyledButton>
            ) : null}
          </React.Fragment>
        ));
      }

      return productList.map((product, index) => (
        <React.Fragment key={product.master_product_id}>
          <div ref={ref => addRef(product.master_product_id, ref)}>
            <ProductCell
              gridPosition={index + 1}
              product={product}
              hideColorsAvailable={hideColorsAvailable}
              hideFlags={hideFlags}
              hideOverlay={hideOverlay}
              hideReviewStars={hideReviewStars}
              hidePromo={hidePromo}
              hidePrice={hidePrice}
              shouldShowProductColor={shouldShowProductColor}
              showShadow={variantStyle}
              extraQueryParams={extraQueryParams}
              onProductClick={onProductClick}
              shouldNotBeClickable={shouldNotBeClickable}
              trackingSource={trackingSource}
              shouldShowSpacedBorder={shouldShowSpacedBorder}
            />
          </div>
          {shouldShowCta ? (
            <StyledButton secondary onClick={() => onCtaClick(product)}>
              {ctaText}
            </StyledButton>
          ) : null}
        </React.Fragment>
      ));
    },
    [
      ctaText,
      displayNewProductPricing,
      elementsToHide,
      extraQueryParams,
      hideColorsAvailable,
      hideFlags,
      hideOverlay,
      hidePrice,
      hidePromo,
      hideReviewStars,
      onCtaClick,
      onProductClick,
      productList,
      shouldNotBeClickable,
      shouldShowCta,
      shouldShowProductColor,
      shouldShowSpacedBorder,
      shouldUseBmigComponents,
      trackingSource,
      variantStyle,
    ]
  );

  const anchorImageForPairItWith = shouldUsePairItWith && (
    <CurrentItemWrapper>
      <CurrentItemLabel>
        <FormattedMessage
          id="site_pdp.product_recommendations_current_item_title"
          defaultMessage="CURRENT ITEM"
        />
      </CurrentItemLabel>
      <StyledProductImage product={product} angle="LAYDOWN" size={8} lazyLoad />
    </CurrentItemWrapper>
  );

  const productCarousel = useMemo(() => {
    return (
      <ProductList
        sliderRef={carouselElementRef}
        listId={pSource}
        products={productList}
        options={options}
        $isPairItWith={shouldUsePairItWith}
      >
        {renderProductSlides}
      </ProductList>
    );
  }, [
    carouselElementRef,
    options,
    pSource,
    productList,
    renderProductSlides,
    shouldUsePairItWith,
  ]);

  return (
    <StyleContext context="recommendations">
      <Container className={className} $isPairItWith={shouldUsePairItWith}>
        {!hideTitle && !shouldUsePairItWith && (
          <Title variantStyle={variantStyle}>{title}</Title>
        )}

        {shouldUsePairItWith && (
          <ModifiedMobileRecommendationsTitle variantStyle={variantStyle}>
            {title}
          </ModifiedMobileRecommendationsTitle>
        )}
        {anchorImageForPairItWith}
        {shouldUsePairItWith ? (
          <CarouselWrapper>
            {!hideTitle && (
              <ModifiedRecommendationsTitle variantStyle={variantStyle}>
                {title}
              </ModifiedRecommendationsTitle>
            )}
            {productCarousel}
          </CarouselWrapper>
        ) : (
          productCarousel
        )}
      </Container>
    </StyleContext>
  );
};

ProductRecommendations.propTypes = {
  maxProducts: PropTypes.number,
  className: PropTypes.string,
  ctaText: PropTypes.node,
  hideColorsAvailable: PropTypes.bool,
  hideFlags: PropTypes.bool,
  hideOverlay: PropTypes.bool,
  hidePrice: PropTypes.bool,
  hidePromo: PropTypes.bool,
  hideReviewStars: PropTypes.bool,
  hideTitle: PropTypes.bool,
  onCtaClick: PropTypes.func,
  onProductClick: PropTypes.func,
  options: PropTypes.object, // Option object to pass to the carousel.
  product: PropTypes.object,
  products: PropTypes.array.isRequired, // The recommended products to show.
  pSource: PropTypes.string,
  title: PropTypes.node,
  trackingSource: PropTypes.string,
  shouldNotBeClickable: PropTypes.bool,
  shouldShowCta: PropTypes.bool,
  shouldShowProductColor: PropTypes.bool,
  shouldShowSpacedBorder: PropTypes.bool,
  shouldUseBmigComponents: PropTypes.bool,
  variantStyle: PropTypes.bool,
  shouldUsePairItWith: PropTypes.bool,
};

ProductRecommendations.defaultProps = {
  maxProducts: -1,
  carouselElementRef: () => {},
  hideColorsAvailable: true,
  hideFlags: true,
  hideOverlay: true,
  hidePrice: true,
  hidePromo: true,
  hideReviewStars: true,
  hideTitle: false,
  onCtaClick: () => {},
  onProductClick: () => {},
  shouldUseBmigComponents: false,
  shouldShowCta: false,
  shouldShowProductColor: false,
  shouldShowSpacedBorder: false,
  title: (
    <FormattedMessage
      id="site_pdp.product_recommendations_title"
      defaultMessage="Wear It With"
    />
  ),
  variantStyle: false,
};

ProductRecommendations.CarouselWrapper = CarouselWrapper;
ProductRecommendations.Container = Container;
ProductRecommendations.CurrentItemLabel = CurrentItemLabel;
ProductRecommendations.CurrentItemWrapper = CurrentItemWrapper;
ProductRecommendations.ModifiedRecommendationsTitle =
  ModifiedRecommendationsTitle;
ProductRecommendations.ProductList = ProductList;
ProductRecommendations.StyledButton = StyledButton;
ProductRecommendations.Title = Title;

export default ProductRecommendations;
